import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationContext = createContext({
  unreadTotal: 0, smsUnread: 0, refreshUnread: () => {},
  recent: [], markAllRead: () => {}, clearAll: () => {},
});

const RECENT_STORAGE_KEY = "hbf_notifications_v1";
const LEGACY_RECENT_KEYS = ["vocalis_notifications_v1"];

function _readRecent() {
  try {
    let raw = localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) {
      for (const legacy of LEGACY_RECENT_KEYS) {
        const v = localStorage.getItem(legacy);
        if (v) { raw = v; localStorage.setItem(RECENT_STORAGE_KEY, v); localStorage.removeItem(legacy); break; }
      }
    }
    return JSON.parse(raw || "[]");
  } catch { return []; }
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [smsUnread, setSmsUnread] = useState(0);
  const [recent, setRecent] = useState(() => _readRecent());
  const lastSeenIdRef = useRef(null);
  const lastSmsIdRef = useRef(null);
  const initialisedRef = useRef(false);
  const smsInitialisedRef = useRef(false);

  const pushNotification = useCallback((n) => {
    setRecent((prev) => {
      // de-dupe by id
      if (prev.some((p) => p.id === n.id)) return prev;
      const next = [n, ...prev].slice(0, 20);
      try { localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const markAllRead = useCallback(async () => {
    // Optimistic local update.
    setRecent((prev) => {
      const next = prev.map((n) => ({ ...n, read: true }));
      try { localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    // Sync to server so a page refresh doesn't resurrect the unread badge.
    try { await api.post("/notifications/mark-all-read"); } catch { /* ignore */ }
  }, []);

  const clearAll = useCallback(async () => {
    setRecent([]);
    try { localStorage.removeItem(RECENT_STORAGE_KEY); } catch { /* ignore */ }
    try { await api.delete("/notifications/clear"); } catch { /* ignore */ }
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const [tc, sms, feed] = await Promise.all([
        api.get("/team-chat/unread-count"),
        api.get("/sms/unread-count").catch(() => ({ data: { total: 0, latest: null } })),
        api.get("/notifications/feed").catch(() => ({ data: { items: [], unread: 0 } })),
      ]);
      const data = tc.data;
      setUnreadTotal(data.total || 0);
      setSmsUnread(sms.data.total || 0);

      // ── Merge server-authoritative persistent notifications into the
      // bell feed. This is the durable store that survives page refreshes,
      // browser switches, and being offline when the notification fired.
      // (Previous versions only used localStorage → notifications were lost
      // if the user wasn't at their desk when they arrived.)
      const serverItems = (feed.data?.items || []).map((it) => ({
        id: `srv-${it.id}`,
        server_id: it.id,
        kind: it.kind === "team_chat" ? "team"
             : it.kind === "meeting_invite"    ? "meeting"
             : it.kind === "meeting_reminder"  ? "meeting"
             : it.kind,
        isMention: !!(it.meta?.mentions || []).includes(user.id),
        title: it.title,
        body: it.body,
        time: it.created_at,
        href: it.href,
        read: !!it.read_at,
      }));
      // Preserve any local-only items we generated (e.g. call events not yet
      // persisted server-side); merge by id.
      setRecent((prev) => {
        const local = prev.filter((n) => !n.server_id);
        const merged = [...serverItems, ...local];
        // Sort by time desc, cap at 30.
        merged.sort((a, b) => (b.time || "").localeCompare(a.time || ""));
        const next = merged.slice(0, 30);
        try { localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });

      const onMessagesPage = location.pathname.startsWith("/app/sms");

      // ----- Team chat toast -----
      const latest = data.latest;
      if (latest) {
        if (!initialisedRef.current) {
          lastSeenIdRef.current = latest.id;
          initialisedRef.current = true;
        } else if (latest.id !== lastSeenIdRef.current) {
          lastSeenIdRef.current = latest.id;
          const isMention = !!data.latest_is_mention;
          // Build a deep-link to the exact thread + message.
          // DM thread_key looks like "{userA}:{userB}" (sorted); group thread_key is "group:{gid}".
          const tkey = latest.thread_key || "";
          let threadParam = "";
          if (tkey.startsWith("group:")) {
            threadParam = `&thread=${encodeURIComponent(tkey)}`;
          } else if (latest.from_user_id) {
            threadParam = `&thread=user:${encodeURIComponent(latest.from_user_id)}`;
          }
          const msgParam = latest.id ? `&msg=${encodeURIComponent(latest.id)}` : "";
          const href = `/app/sms?tab=team${threadParam}${msgParam}`;
          pushNotification({
            id: `team-${latest.id}`,
            kind: "team",
            isMention,
            title: isMention
              ? `Mention from ${latest.from_user_name || "Someone"}`
              : (latest.from_user_name || "New team message"),
            body: latest.body || "(attachment)",
            time: latest.created_at || new Date().toISOString(),
            href,
            read: false,
          });
          if (!onMessagesPage) {
            toast(
              isMention ? `🔔 ${latest.from_user_name || "Someone"} mentioned you` : (latest.from_user_name || "New message"),
              {
                description: (latest.body || "").length > 80 ? (latest.body || "").slice(0, 80) + "…" : (latest.body || "(attachment)"),
                duration: isMention ? 10000 : 6000,
                position: "top-right",
                className: isMention ? "ring-2 ring-[var(--accent)]" : undefined,
                action: { label: "Open", onClick: () => navigate(href) },
              }
            );
          }
          try {
            if ("Notification" in window && Notification.permission === "granted" && !onMessagesPage) {
              const n = new Notification(
                isMention ? `🔔 Mention from ${latest.from_user_name || "Someone"}` : (latest.from_user_name || "New message"),
                { body: (latest.body || "").slice(0, 140), tag: latest.thread_key || latest.id, icon: "/favicon.ico", requireInteraction: isMention }
              );
              n.onclick = () => { window.focus(); navigate(href); n.close(); };
            }
          } catch { /* ignore */ }
        }
      } else {
        initialisedRef.current = true;
      }

      // ----- Customer SMS toast -----
      const smsLatest = sms.data.latest;
      if (smsLatest) {
        if (!smsInitialisedRef.current) {
          lastSmsIdRef.current = smsLatest.id;
          smsInitialisedRef.current = true;
        } else if (smsLatest.id !== lastSmsIdRef.current) {
          lastSmsIdRef.current = smsLatest.id;
          const contactParam = smsLatest.contact_id ? `&contact=${encodeURIComponent(smsLatest.contact_id)}` : "";
          const msgParam = smsLatest.id ? `&msg=${encodeURIComponent(smsLatest.id)}` : "";
          const href = `/app/sms?tab=sms${contactParam}${msgParam}`;
          pushNotification({
            id: `sms-${smsLatest.id}`,
            kind: "sms",
            isMention: false,
            title: `SMS from ${smsLatest.from_display_name || "Unknown"}`,
            body: smsLatest.body || "",
            time: smsLatest.created_at || new Date().toISOString(),
            href,
            read: false,
          });
          if (!onMessagesPage) {
            toast(
              `📱 SMS from ${smsLatest.from_display_name || "Unknown"}`,
              {
                description: (smsLatest.body || "").length > 80 ? (smsLatest.body || "").slice(0, 80) + "…" : smsLatest.body,
                duration: 6000,
                position: "top-right",
                action: { label: "Open", onClick: () => navigate(href) },
              }
            );
          }
          try {
            if ("Notification" in window && Notification.permission === "granted" && !onMessagesPage) {
              const n = new Notification(
                `📱 SMS from ${smsLatest.from_display_name || "Unknown"}`,
                { body: (smsLatest.body || "").slice(0, 140), tag: `sms-${smsLatest.id}`, icon: "/favicon.ico" }
              );
              n.onclick = () => { window.focus(); navigate(href); n.close(); };
            }
          } catch { /* ignore */ }
        }
      } else {
        smsInitialisedRef.current = true;
      }
    } catch (e) { /* silent — polling */ }
  }, [user, location.pathname, navigate]);

  // Request desktop-notification permission once after login
  useEffect(() => {
    if (!user) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, [user]);

  // Poll every 6s
  useEffect(() => {
    if (!user) {
      setUnreadTotal(0);
      setSmsUnread(0);
      lastSeenIdRef.current = null;
      lastSmsIdRef.current = null;
      initialisedRef.current = false;
      smsInitialisedRef.current = false;
      return;
    }
    refreshUnread();
    const id = setInterval(refreshUnread, 6000);
    return () => clearInterval(id);
  }, [user, refreshUnread]);

  return (
    <NotificationContext.Provider value={{ unreadTotal, smsUnread, refreshUnread, recent, markAllRead, clearAll, pushNotification }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
